// src/components/settings/RoleMatrix.jsx
//
// Settings > Roles: one row per permission, one toggle per client role.
// Locked cells (client admin always on, "Site dashboard" for everyone,
// "Manage users" admins only) are enforced by the server too.
// Desktop: a table-like grid with role columns. Phone: each permission is a
// card with its three role toggles in one row.

import React from 'react';

function isLocked(perm, role) {
  return Array.isArray(perm.fixed) || role === 'admin';
}

export default function RoleMatrix({ setting, value, saved, onChange, disabled }) {
  const { roles, permissions } = setting;

  const toggle = (permKey, role) => {
    const current = value[permKey] || [];
    const next = current.includes(role) ? current.filter((r) => r !== role) : [...current, role];
    onChange({ ...value, [permKey]: roles.map((r) => r.id).filter((r) => next.includes(r)) });
  };

  return (
    <div className="st-matrix" role="table" aria-label={setting.label}>
      <div className="st-matrix-row st-matrix-head" role="row">
        <span role="columnheader" className="st-matrix-perm-h">Can…</span>
        {roles.map((r) => <span key={r.id} role="columnheader" className="st-matrix-role-h">{r.label}</span>)}
      </div>
      {permissions.map((p) => {
        const on = value[p.key] || [];
        const was = saved[p.key] || [];
        return (
          <div key={p.key} className="st-matrix-row" role="row">
            <div className="st-matrix-perm" role="rowheader">
              <span className="st-matrix-label">{p.label}</span>
              <span className="st-matrix-help">{p.help}</span>
            </div>
            {roles.map((r) => {
              const checked = on.includes(r.id);
              const locked = isLocked(p, r.id);
              const changed = checked !== was.includes(r.id);
              return (
                <div key={r.id} role="cell" className="st-matrix-cell">
                  <button
                    type="button"
                    className={`st-cell ${checked ? 'is-on' : ''} ${locked ? 'is-locked' : ''} ${changed ? 'is-changed' : ''}`}
                    aria-pressed={checked}
                    aria-label={`${r.label}: ${p.label}${locked ? ' (fixed)' : ''}`}
                    disabled={disabled || locked}
                    title={locked ? (r.id === 'admin' && !p.fixed ? 'Client admins always have this' : 'Fixed for safety') : undefined}
                    onClick={() => toggle(p.key, r.id)}
                  >
                    <span className="st-cell-role">{r.label}</span>
                    <span className="st-cell-icons" aria-hidden="true">
                      <i className={`fas ${checked ? 'fa-check' : 'fa-minus'}`}></i>
                      {locked && <i className="fas fa-lock st-cell-lock"></i>}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// For the confirm sheet and History: which permissions changed, old -> new roles.
export function matrixDiff(setting, oldValue, newValue) {
  const names = Object.fromEntries((setting.roles || []).map((r) => [r.id, r.label]));
  const fmt = (list) => (list && list.length ? list.map((r) => names[r] || r).join(', ') : 'Nobody');
  return (setting.permissions || [])
    .filter((p) => JSON.stringify((oldValue || {})[p.key] || []) !== JSON.stringify((newValue || {})[p.key] || []))
    .map((p) => ({ key: p.key, label: p.label, from: fmt((oldValue || {})[p.key]), to: fmt((newValue || {})[p.key]) }));
}
